import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Menu, X, Watch, Ruler } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Header = ({ onSizeGuideClick }) => {
  const { getCartCount, setIsCartOpen } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cartCount = getCartCount();

  const navLinks = [
    { label: 'Collections', href: '#collections' },
    { label: 'Heritage', href: '#heritage' },
    { label: 'Testimonials', href: '#testimonials' },
  ];

  return (
    <header className="fixed top-0 w-full z-40 border-b border-white/5 bg-black/80 backdrop-blur-md" data-testid="header">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo">
            <Watch className="w-8 h-8 text-gold-500" />
            <span className="font-serif text-2xl text-white tracking-tight">CHRONOS</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-sm uppercase tracking-[0.2em] text-white/60 hover:text-gold-500 transition-colors"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={onSizeGuideClick}
              className="text-sm uppercase tracking-[0.2em] text-white/60 hover:text-gold-500 transition-colors flex items-center gap-2"
              data-testid="nav-size-guide"
            >
              <Ruler className="w-4 h-4" />
              Size Guide
            </button>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative text-white/80 hover:text-gold-500 hover:bg-transparent"
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-button"
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold-500 text-black text-xs font-bold rounded-full flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Button>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white/80 hover:text-gold-500 hover:bg-transparent" data-testid="mobile-menu-button">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-charcoal border-white/5 w-[300px]">
                <div className="flex flex-col gap-8 pt-8">
                  {navLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="text-lg text-white/80 hover:text-gold-500 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                  <button
                    onClick={() => {
                      onSizeGuideClick();
                      setMobileMenuOpen(false);
                    }}
                    className="text-lg text-white/80 hover:text-gold-500 transition-colors flex items-center gap-2"
                  >
                    <Ruler className="w-5 h-5" />
                    Size Guide
                  </button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
